/**
 * The body of the Team details page: team facts, headline numbers, and one card
 * per member answering "who is doing what" — their figures for the period, what
 * is next on their list (overdue in red), what they finished last, and their
 * biggest open deals. Data: GET /teams/:id/summary.
 */
import { Link } from 'react-router-dom';
import { Card, StatTile, Badge, Avatar, EmptyState } from '../../components/common/index.jsx';
import { Spinner } from '../../components/common/Loading.jsx';
import { moneyShort, number, dateTime, relativeTime } from '../../utils/formatters.js';
import { StatusBadge } from './performanceShared.jsx';

const KIND_ICON = {
  call: 'bi-telephone-fill', visit: 'bi-geo-alt-fill', meeting: 'bi-people-fill', demo: 'bi-easel-fill',
  email: 'bi-envelope-fill', task: 'bi-check2-square', note: 'bi-sticky-fill',
};
const PERIODS = [['month', 'This month'], ['quarter', 'This quarter'], ['year', 'This year']];

function Figure({ label, value, warn }) {
  return (
    <div className="text-center flex-fill">
      <div className={`fw-750 tabular${warn ? ' text-danger' : ''}`}>{value}</div>
      <div className="t-2xs ink-4">{label}</div>
    </div>
  );
}

function ActivityRow({ a, overdueStyle }) {
  return (
    <li className="d-flex align-items-start gap-2 mb-1">
      <i className={`bi ${KIND_ICON[a.kind] ?? 'bi-dot'} ink-4 mt-1`} aria-hidden="true" style={{ fontSize: 12 }} />
      <div className="min-w-0 flex-grow-1">
        <div className="t-xs fw-600 text-truncate">{a.subject || a.kind}</div>
        <div className={`t-2xs ${overdueStyle && a.overdue ? 'text-danger fw-650' : 'ink-4'}`}>
          {overdueStyle ? (a.overdue ? `Overdue · ${relativeTime(a.at)}` : dateTime(a.at)) : relativeTime(a.at)}
        </div>
      </div>
    </li>
  );
}

function MemberCard({ m, canEdit, removing, onRemove }) {
  const idle = m.lastActivityAt ? Math.floor((Date.now() - new Date(m.lastActivityAt).getTime()) / 86_400_000) : null;
  return (
    <Card className="h-100">
      <div className="d-flex align-items-center gap-2 mb-3">
        <Avatar name={m.name} size={36} />
        <div className="min-w-0 flex-grow-1">
          <div className="fw-700 text-truncate">{m.name}</div>
          <div className="t-2xs ink-4 text-truncate">
            {idle == null ? 'No activity yet' : idle === 0 ? 'Active today' : `Last activity ${idle} day${idle === 1 ? '' : 's'} ago`}
          </div>
        </div>
        {m.isLead && <Badge tone="sky">Manager</Badge>}
        {m.target > 0 && <StatusBadge status={m.status} />}
        {canEdit && !m.isLead && (
          <button
            type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }}
            title="Remove from team" disabled={removing} onClick={() => onRemove(m.id)}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="d-flex gap-1 pb-3 mb-3 border-bottom">
        <Figure label="Open deals" value={m.openDeals} />
        <Figure label="Pipeline" value={moneyShort(m.pipeline)} />
        <Figure label="Open leads" value={m.openLeads} />
        <Figure label="Calls" value={m.calls} />
        <Figure label="Visits" value={m.visits} />
        <Figure label="Meetings" value={m.meetings} />
        <Figure label="Overdue" value={m.overdue} warn={m.overdue > 0} />
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="eyebrow mb-2">Up next</div>
          {m.upcoming.length ? <ul className="list-unstyled mb-0">{m.upcoming.map((a) => <ActivityRow key={a.id} a={a} overdueStyle />)}</ul>
            : <div className="t-xs ink-4">Nothing scheduled</div>}
        </div>
        <div className="col-md-4">
          <div className="eyebrow mb-2">Recently done</div>
          {m.recent.length ? <ul className="list-unstyled mb-0">{m.recent.map((a) => <ActivityRow key={a.id} a={a} />)}</ul>
            : <div className="t-xs ink-4">Nothing completed yet</div>}
        </div>
        <div className="col-md-4">
          <div className="eyebrow mb-2">Biggest open deals</div>
          {m.topDeals.length ? (
            <ul className="list-unstyled mb-0">
              {m.topDeals.map((d) => (
                <li key={d.id} className="mb-1">
                  <Link to="/opportunities" className="t-xs fw-600 d-block text-truncate">{d.name}</Link>
                  <div className="t-2xs ink-4 text-truncate">{d.account ? `${d.account} · ` : ''}{moneyShort(d.amount)}</div>
                </li>
              ))}
            </ul>
          ) : <div className="t-xs ink-4">No open deals</div>}
        </div>
      </div>
      <div className="mt-3 text-end">
        <Link to={`/team-performance/${m.id}`} className="t-xs fw-650">
          Full performance <i className="bi bi-arrow-right" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  );
}

export function TeamSummary({
  team, summary, loading, period, onPeriod, canEdit, userOptions, updateTeam, removing, onRemove, rosterLoading, rosterCount,
}) {
  const h = summary?.headline;
  const done = h ? h.calls + h.visits + h.meetings + h.emails : 0;

  return (
    <>
      <Card className="mb-3">
        <div className="d-flex flex-wrap align-items-center gap-4">
          <div><div className="eyebrow">Code</div><div className="fw-650">{team.code}</div></div>
          <div>
            <div className="eyebrow">Manager</div>
            {canEdit ? (
              <select
                className="form-select form-select-sm" value={team.manager?.id ?? ''} disabled={updateTeam.isPending}
                onChange={(e) => e.target.value && updateTeam.mutate({
                  id: team.id, patch: { manager_id: e.target.value }, rowVersion: team.rowVersion,
                })}
              >
                {!team.manager && <option value="">Select…</option>}
                {userOptions.users.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            ) : <div className="fw-650">{team.manager?.full_name ?? '—'}</div>}
          </div>
          <div><div className="eyebrow">Business unit</div><div className="fw-650">{team.businessUnit ?? '—'}</div></div>
          <div><div className="eyebrow">Status</div><Badge tone={team.statusTone}>{team.statusLabel}</Badge></div>
          <div><div className="eyebrow">Members</div><div className="fw-650">{rosterLoading ? '…' : rosterCount}</div></div>
          <div className="ms-auto">
            <div className="btn-group" role="group" aria-label="Period">
              {PERIODS.map(([k, label]) => (
                <button
                  key={k} type="button"
                  className={`btn btn-sm-x ${period === k ? 'btn-add' : 'btn-soft'}`} onClick={() => onPeriod(k)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {loading && !summary ? <Spinner label="Loading team activity" /> : !summary?.members?.length ? (
        <EmptyState icon="bi-people" title="No members yet" message="Add people to this team with the button above." />
      ) : (
        <>
          <div className="row g-3 mb-3">
            <div className="col-6 col-xl"><StatTile label="Pipeline" value={moneyShort(h.pipeline)} icon="bi-funnel-fill" tone="brand" meta={`${h.openDeals} open deals`} /></div>
            <div className="col-6 col-xl"><StatTile label="Open leads" value={number(h.openLeads)} icon="bi-magnet-fill" tone="sky" meta={`${h.members} members`} /></div>
            <div className="col-6 col-xl"><StatTile label="Activities done" value={number(done)} icon="bi-check2-circle" tone="emerald" meta={`${h.calls} calls · ${h.visits} visits · ${h.meetings} meetings`} /></div>
            <div className="col-6 col-xl"><StatTile label="Overdue follow-ups" value={number(h.overdue)} icon="bi-exclamation-triangle-fill" tone="rose" meta="Across the team" /></div>
          </div>
          <div className="eyebrow mb-2">Who is doing what · {summary.period.label}</div>
          <div className="row g-3">
            {summary.members.map((m) => (
              <div key={m.id} className="col-12 col-xxl-6">
                <MemberCard m={m} canEdit={canEdit} removing={removing} onRemove={onRemove} />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
